import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import DeviceLoadingSkeleton from '../../devices/shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import {
  useEnergyStorageKineticSystemDetailQuery,
  useEnergyStorageKineticSystemDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import { PanelFrame, ViewMode } from '../../devices/shared-ui/panel-frame';
import moment from 'moment';
import { DeviceInformation } from '../../devices/shared-ui/device-info';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import { EnergyControllerComponent } from '../../devices/virtual-power-plant/energy-controller';
import { EnergyInverterComponent } from '../../devices/virtual-power-plant/energy-inverter';
import { EnergyStorageCoolingSystemComponent } from '../../devices/virtual-power-plant/energy-storage-cooling-system';
import { FireSuppressionSystemComponent } from '../../devices/virtual-power-plant/fire-suppression-system';
import { EnergyConverterComponent } from '../../devices/virtual-power-plant/energy-converter';
import { KineticEnergyManagementSystemComponent } from '../../devices/virtual-power-plant/kinetic-energy-management-system';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

interface EnergyStorageKineticSystemProps {
  id: string;
}

export const EnergyStorageKineticSystemComponent: React.FC<
  EnergyStorageKineticSystemProps
> = ({ id }) => {
  const [participant, setParticipant] = React.useState<any>();
  const { data, loading, error } = useEnergyStorageKineticSystemDetailQuery({
    variables: { id },
  });

  useEnergyStorageKineticSystemDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  React.useEffect(() => {
    if (data?.energyStorageKineticSystemById) {
      setParticipant(data.energyStorageKineticSystemById);
    }
  }, [data]);

  if (loading) return <DeviceLoadingSkeleton size="large" />;
  if (error)
    return (
      <Typography color="error">Error loading participant data</Typography>
    );
  if (!participant) return null;

  const participantInfo: DeviceInformation[] = [
    {
      'System Name': participant.name || 'Unnamed Kinetic Storage System',
      Location: participant.location || 'Unknown',
      'Last Updated': moment(participant.timestamp).fromNow(),
      'Power State': participant.powerState?.value ? 'On' : 'Off',
      'Connection Status': participant.connected?.value
        ? 'Connected'
        : 'Disconnected',
      'Last Connected': moment(participant.lastConnectedAt?.value).fromNow(),
      'Maximum Energy Capacity': formatDeviceMetric({
        value: participant.maximumEnergyCapacity?.value ?? undefined,
        units: participant.maximumEnergyCapacity?.units ?? undefined,
      }),
      'Maintenance Status': participant.maintenanceScheduleStatus?.value
        ? 'Active'
        : 'Inactive',
      'Flywheel Speed': formatDeviceMetric({
        value: participant.flywheelSpeed?.value ?? undefined,
        units: participant.flywheelSpeed?.units ?? undefined,
      }),
      'Charging Rate': formatDeviceMetric({
        value: participant.chargingRate?.value ?? undefined,
        units: participant.chargingRate?.units ?? undefined,
      }),
      Temperature: formatDeviceMetric({
        value: participant.temperature?.value ?? undefined,
        units: participant.temperature?.units ?? undefined,
      }),
      'Vibration Level': formatDeviceMetric({
        value: participant.vibrationLevel?.value ?? undefined,
        units: participant.vibrationLevel?.units ?? undefined,
      }),
      'Bearing Status': participant.bearingStatus?.value ? 'Normal' : 'Warning',
      'Vacuum Level': formatDeviceMetric({
        value: participant.vacuumLevel?.value ?? undefined,
        units: participant.vacuumLevel?.units ?? undefined,
      }),
      Property: participant.property?.title || 'Not assigned',
    },
  ];

  return (
    <PanelFrame
      title="Energy Storage Kinetic System"
      iconType="material-symbols:storage"
      panel={participant}
      infoEntries={participantInfo}
      viewMode={ViewMode.NORMAL}
      editingEnabled={false}
    >
      <Box sx={{ p: 2 }}>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                System Status
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Power State
                  </Typography>
                  <Typography variant="body1">
                    {participant.powerState?.value ? 'On' : 'Off'}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Connection Status
                  </Typography>
                  <Typography variant="body1">
                    {participant.connected?.value
                      ? 'Connected'
                      : 'Disconnected'}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Maintenance Status
                  </Typography>
                  <Typography variant="body1">
                    {participant.maintenanceScheduleStatus?.value
                      ? 'Active'
                      : 'Inactive'}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Bearing Status
                  </Typography>
                  <Typography variant="body1">
                    {participant.bearingStatus?.value ? 'Normal' : 'Warning'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Performance Metrics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Maximum Energy Capacity
                  </Typography>
                  <Typography variant="body1">
                    {formatDeviceMetric({
                      value:
                        participant.maximumEnergyCapacity?.value ?? undefined,
                      units:
                        participant.maximumEnergyCapacity?.units ?? undefined,
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Flywheel Speed
                  </Typography>
                  <Typography variant="body1">
                    {formatDeviceMetric({
                      value: participant.flywheelSpeed?.value ?? undefined,
                      units: participant.flywheelSpeed?.units ?? undefined,
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Charging Rate
                  </Typography>
                  <Typography variant="body1">
                    {formatDeviceMetric({
                      value: participant.chargingRate?.value ?? undefined,
                      units: participant.chargingRate?.units ?? undefined,
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Temperature
                  </Typography>
                  <Typography variant="body1">
                    {formatDeviceMetric({
                      value: participant.temperature?.value ?? undefined,
                      units: participant.temperature?.units ?? undefined,
                    })}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                System Health
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Vibration Level
                  </Typography>
                  <Typography variant="body1">
                    {formatDeviceMetric({
                      value: participant.vibrationLevel?.value ?? undefined,
                      units: participant.vibrationLevel?.units ?? undefined,
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Vacuum Level
                  </Typography>
                  <Typography variant="body1">
                    {formatDeviceMetric({
                      value: participant.vacuumLevel?.value ?? undefined,
                      units: participant.vacuumLevel?.units ?? undefined,
                    })}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {participant.kemsId ? (
            <KineticEnergyManagementSystemComponent id={participant.kemsId} />
          ) : null}
          {participant.controllerId ? (
            <EnergyControllerComponent id={participant.controllerId} />
          ) : null}
          {participant.inverterId ? (
            <EnergyInverterComponent id={participant.inverterId} />
          ) : null}
          {participant.coolingSystemId ? (
            <EnergyStorageCoolingSystemComponent
              id={participant.coolingSystemId}
            />
          ) : null}
          {participant.fireSuppressionSystemId ? (
            <FireSuppressionSystemComponent
              id={participant.fireSuppressionSystemId}
            />
          ) : null}
          {participant.converterId ? (
            <EnergyConverterComponent id={participant.converterId} />
          ) : null}
        </Stack>
      </Box>
    </PanelFrame>
  );
};
